import ApplicationCard from 'Components/campaigns/applications/ApplicationCard';
import 'Components/campaigns/applications/style.scss';
import ApplicationFilters from 'Components/campaigns/applications/ApplicationFilters';
import useDocumentTitle from 'hooks/useDocumentTitle';
import CampaignLayout from 'Layouts/CampaignLayout';
import { useEffect, useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useSearchParams } from 'react-router-dom';
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { invalidateCampaignApplicationLists, updateApplicationFilters } from 'slices/campaigns/applications/reducer';
import { fetchCampaignApplications, revalidateLoadedApplicationPages } from 'slices/campaigns/applications/thunk';
import { AppDispatch, RootState } from 'store';
import { ApplicationFiltersNew, ApplicationsFilterTab, SortOptionLabel, SortOptions } from 'types/campaign/applications';
import { decodeBase64, encodeBase64, generateUniqueKey, getErrorMessage } from 'utils/helpers';
import FilterIcon from 'Components/icons/FilterIcon';
import AppliedFilters from 'Components/campaigns/applications/AppliedFilters';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import TrashIcon from 'assets/icons/TrashIcon';
import { campaignIconSVG } from 'pages/Campaign/Campaign';
import { rejectAllApplications as rejectAllApplicationsAPI, fetchCampaignApplications as fetchCampaignApplicationsAPI } from 'services/api/campaign/application';
import { PRIVILEGE } from 'constants/privilege';
import { usePrivileges } from 'hooks/useAuth';
import { buildApplicationsCsv, downloadCsvFile } from 'utils/applicationCsvExport';

const pinBookmarked = (filters: ApplicationFiltersNew, bookmarked: 'y' | 'n'): ApplicationFiltersNew => ({
    ...filters,
    bookmarked,
});

export interface CampaignApplicationsListProps {
    bookmarked: 'y' | 'n';
    tabDefaultFilters: ApplicationFiltersNew;
    resetFilters: ApplicationFiltersNew;
    documentTitle: string;
    emptyStateMessage: string;
    viewBestCreative?: boolean;
    sidebar?: React.ReactNode;
    hasTransactions?: boolean;
}

const CampaignApplicationsList: React.FC<CampaignApplicationsListProps> = ({
    bookmarked,
    tabDefaultFilters,
    resetFilters,
    documentTitle,
    emptyStateMessage,
    viewBestCreative = false,
    sidebar,
    hasTransactions = false,
}) => {
    useDocumentTitle(documentTitle);
    const [searchParams, setSearchParams] = useSearchParams();
    const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
    const revalidateInFlight = useRef(false);

    const queryFilters = searchParams.get('filters');
    const dispatch: AppDispatch = useDispatch();
    const filterTab: ApplicationsFilterTab = bookmarked === 'y' ? 'shortlisted' : 'applications';

    useEffect(() => {
        const decodedFilters = queryFilters ? decodeBase64(queryFilters) : tabDefaultFilters;
        dispatch(
            updateApplicationFilters({
                tab: filterTab,
                filters: { ...pinBookmarked(decodedFilters, bookmarked), page: 1 },
            })
        );
    }, [queryFilters, bookmarked, dispatch, tabDefaultFilters, filterTab]);

    const { campaignId } = useParams();
    const [searchQuery, setSearchQuery] = useState('');
    const filters = useSelector((state: RootState) =>
        filterTab === 'shortlisted'
            ? state.campaignApplications.filtersShortlisted
            : state.campaignApplications.filtersApplications
    );
    const pinnedFilters = useMemo(() => pinBookmarked(filters, bookmarked), [filters, bookmarked]);
    const listFilterId = useMemo(() => generateUniqueKey(pinnedFilters, ['page']), [pinnedFilters]);
    const [filterId, setFilterId] = useState(listFilterId);

    const listCache = useSelector(
        (state: RootState) =>
            state.campaignApplications.campaigns[campaignId!]?.filtered_lists?.[listFilterId]
    );
    const applicationsData = listCache;
    const loading = useSelector((state: RootState) => state.campaignApplications.loading);
    const revalidating = useSelector((state: RootState) => state.campaignApplications.revalidating);
    const pendingRevalidateMaxPage = listCache?.pendingRevalidateMaxPage;
    const applications = applicationsData?.list ?? [];
    const page = applicationsData?.page ?? 1;
    const has_more = applicationsData?.has_more ?? true;
    const filteredCount = applicationsData?.count ?? 0;
    const error = useSelector((state: RootState) => state.campaignApplications.error);
    const [initialLoading, setInitialLoading] = useState(true);

    const setFilters = (updatedFilters: ApplicationFiltersNew) => {
        const encodedFilters = encodeBase64(pinBookmarked(updatedFilters, bookmarked));
        setSearchParams({ filters: encodedFilters });
    };

    useEffect(() => {
        setSearchQuery(filters.q ?? '');
    }, [filters.q, filterTab]);

    const [filtersModalVisibility, showFiltersModal] = useState(false);
    const [rejectingAll, setRejectingAll] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const privileges = usePrivileges();

    useEffect(() => {
        if (campaignId) {
            const hasExistingItems = (listCache?.list?.length ?? 0) > 0;
            const isStaleRefresh = listCache?.expired === true;
            const isBookmarkRefresh =
                isStaleRefresh && hasExistingItems && (listCache.page ?? 1) <= 1;
            const isFilterOrSearchChange = pinnedFilters.page === 1 && !isBookmarkRefresh;

            if ((isFilterOrSearchChange && !hasExistingItems) || (isStaleRefresh && !hasExistingItems)) {
                setInitialLoading(true);
            }
            if (debounceTimeout.current) {
                clearTimeout(debounceTimeout.current);
            }
            const delay = listCache?.expired ? 0 : 500;
            debounceTimeout.current = setTimeout(() => {
                const forceRefresh = listCache?.expired === true;
                const fetchFilters = forceRefresh
                    ? { ...pinnedFilters, page: 1 }
                    : pinnedFilters;

                dispatch(
                    fetchCampaignApplications({
                        filters: fetchFilters,
                        campaignId,
                        force: forceRefresh,
                    })
                ).finally(() => {
                    setFilterId(listFilterId);
                    setInitialLoading(false);
                });
            }, delay);
        }
    }, [pinnedFilters, campaignId, dispatch, listCache?.expired, listFilterId]);

    useEffect(() => {
        if (!campaignId || !pendingRevalidateMaxPage || revalidating || revalidateInFlight.current) {
            return;
        }

        revalidateInFlight.current = true;
        const scrollEl = document.querySelector('.campaign-page-content') as HTMLElement | null;
        const scrollTop = scrollEl?.scrollTop ?? window.scrollY;
        const filtersForRevalidate = listCache?.filters ?? pinnedFilters;

        dispatch(
            revalidateLoadedApplicationPages({
                campaignId,
                filters: { ...filtersForRevalidate, page: 1 },
                maxPage: pendingRevalidateMaxPage,
                filterId: listFilterId,
            })
        ).finally(() => {
            revalidateInFlight.current = false;
            requestAnimationFrame(() => {
                if (scrollEl) {
                    scrollEl.scrollTop = scrollTop;
                } else {
                    window.scrollTo(0, scrollTop);
                }
            });
        });
    }, [
        campaignId,
        pendingRevalidateMaxPage,
        revalidating,
        dispatch,
        listFilterId,
        listCache?.filters,
        pinnedFilters,
    ]);

    const nextPage = () => {
        if (has_more && campaignId && !loading && !revalidating) {
            const nextPageNumber = page + 1;
            dispatch(
                fetchCampaignApplications({
                    filters: { ...pinnedFilters, page: nextPageNumber },
                    campaignId,
                })
            );
        }
    };

    const searchTask = (q: string) => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            if (q.trim().length > 3) {
                setFilters({ ...pinnedFilters, q: q.trim(), page: 1 });
            } else if (q.trim().length === 0) {
                setFilters({ ...pinnedFilters, q: '', page: 1 });
            }
        }, 500);
        setSearchQuery(q);
    };

    const showEmptySkeleton = initialLoading && applications.length === 0;
    const gridSkeletonCount = sidebar ? 3 : 4;

    const paginationLoader = (
        <div className="campaign-applications-grid w-100">
            {Array.from({ length: gridSkeletonCount }).map((_, i) => (
                <div key={`load-more-skeleton-${i}`} className="campaign-applications-grid__item">
                    <Skeleton height={336} className="w-100 d-block" />
                </div>
            ))}
        </div>
    );

    const rejectAllApplications = async () => {
        if (!campaignId || rejectingAll) return;

        if (!privileges.includes(PRIVILEGE.CAMPAIGN.APPLICATION.PROCESS)) {
            toast.error("You don't have permission to process applications!");
            return;
        }

        const result = await Swal.fire({
            title: 'Are you sure you want to reject all applications?',
            text: 'This action cannot be undone.',
            iconHtml: campaignIconSVG,
            showCancelButton: true,
            confirmButtonText: 'Yes, Reject All',
            cancelButtonText: 'Cancel',
            reverseButtons: true,
        });

        if (!result.isConfirmed) return;

        setRejectingAll(true);
        try {
            const response = await rejectAllApplicationsAPI(campaignId);
            if (response?.status === false) {
                throw new Error(getErrorMessage(response, 'Failed to reject all applications'));
            }

            dispatch(invalidateCampaignApplicationLists({ campaignId, clearLists: true }));
            dispatch(
                fetchCampaignApplications({
                    campaignId,
                    filters: { ...pinnedFilters, page: 1 },
                    force: true,
                })
            );
            toast.success('All applications have been rejected.');
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to reject all applications'));
        } finally {
            setRejectingAll(false);
        }
    };

    const downloadApplications = async () => {
        if (!campaignId || downloading || filteredCount === 0) return;

        setDownloading(true);
        try {
            const { items } = await fetchCampaignApplicationsAPI(campaignId, {
                ...pinnedFilters,
                page: 1,
                limit: filteredCount,
            });

            const csv = buildApplicationsCsv(items);
            downloadCsvFile(csv, `applications_${campaignId}_${new Date().toISOString().split('T')[0]}.csv`);
            toast.success('Applications downloaded successfully.');
        } catch (error) {
            toast.error(getErrorMessage(error, 'Failed to download applications'));
        } finally {
            setDownloading(false);
        }
    };

    const getFiltersToolbar = () => (
        <div className="applied-filters-toolbar mt-3">
            <button
                type="button"
                className="filter-badge-button applied-filters-toolbar__trigger"
                onClick={() => showFiltersModal(true)}
            >
                <FilterIcon className="me-1" /> Filters <i className="mdi mdi-chevron-down fs-5" />
            </button>
            <AppliedFilters filters={filters} onChange={(updatedFilters) => setFilters({ ...updatedFilters, page: 1 })} />
        </div>
    );

    const getFiltersComponent = () => (
        <div className="d-flex justify-content-end align-items-center">
            <UncontrolledDropdown className="sort-dropdown me-2">
                <DropdownToggle tag="button" className="btn btn-white d-flex justify-content-between align-items-center w-100">
                    <span>
                        <span className="text-muted me-1">Sort by: </span>
                        {SortOptionLabel[pinnedFilters.sort]}
                    </span>
                    <i className="mdi mdi-chevron-down fs-5" />
                </DropdownToggle>
                <DropdownMenu className="mt-1 p-0 w-100">
                    {Object.values(SortOptions).map((sortOption, i) => (
                        <DropdownItem key={i} onClick={() => setFilters({ ...pinnedFilters, sort: sortOption, page: 1 })}>
                            {SortOptionLabel[sortOption]}
                        </DropdownItem>
                    ))}
                </DropdownMenu>
            </UncontrolledDropdown>
            {bookmarked === 'n' && (
                <>
                    <button
                        type="button"
                        title="Download Applications"
                        className="campaign-applications-download-btn me-2"
                        onClick={downloadApplications}
                        disabled={downloading || initialLoading || filteredCount === 0}
                    >
                        {
                            downloading ? <i className="ri-loader-line animate-spin" /> : <i className="ri-file-download-line" />
                        }
                    </button>
                    <button
                        type="button"
                        title="Reject All Applications"
                        className="campaign-applications-reject-all-btn me-2"
                        onClick={rejectAllApplications}
                        disabled={rejectingAll || initialLoading}
                    >
                        {
                            rejectingAll ? <i className="ri-loader-line animate-spin" /> : <TrashIcon size={16} />
                        }
                    </button>
                </>
            )}
            <div className="campaign-layout-search-container position-relative">
                <span>
                    <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M7.66732 14.0002C11.1651 14.0002 14.0007 11.1646 14.0007 7.66683C14.0007 4.16903 11.1651 1.3335 7.66732 1.3335C4.16951 1.3335 1.33398 4.16903 1.33398 7.66683C1.33398 11.1646 4.16951 14.0002 7.66732 14.0002Z"
                            fill="#BBD8ED"
                            stroke="#0069AD"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path d="M15 16L12 13" stroke="#0069AD" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </span>
                <input
                    type="text"
                    placeholder="Search..."
                    value={searchQuery}
                    onChange={(e) => searchTask(e.target.value)}
                    className="campaign-layout-search-input"
                />
                {searchQuery && (
                    <span className="search-clear-icon" onClick={() => searchTask('')}>
                        <i className="bx bx-x" />
                    </span>
                )}
            </div>
        </div>
    );

    const applicationsGrid = (
        <InfiniteScroll
            dataLength={applications.length}
            next={nextPage}
            className="campaign-applications-grid"
            hasMore={has_more}
            loader={(loading || revalidating) && applications.length > 0 ? paginationLoader : <></>}
        >
            {applications.map((application) => (
                <div key={application.uuid} className="campaign-applications-grid__item">
                    <ApplicationCard campaignId={campaignId!} application={application} hasTransactions={false} viewBestCreative={viewBestCreative} />
                </div>
            ))}
            {showEmptySkeleton &&
                Array.from({ length: gridSkeletonCount }).map((_, i) => (
                    <div key={`empty-skeleton-${i}`} className="campaign-applications-grid__item">
                        <Skeleton height={336} className="w-100 d-block" />
                    </div>
                ))}
            {!loading && !initialLoading && error && (
                <div className="d-flex justify-content-center align-items-center campaign-applications-grid__span-all">
                    <button type="button" className="theme-btn theme-btn-sm btn-primary-outlined" onClick={nextPage}>
                        Load More <i className="ms-2 fs-5 bx bx-refresh"></i>
                    </button>
                </div>
            )}
            {!loading && !initialLoading && applications.length === 0 && (
                <div className="d-flex w-100 justify-content-center align-items-center campaign-applications-grid__span-all">
                    <div className="length-zero">
                        <h3>{emptyStateMessage}</h3>
                    </div>
                </div>
            )}
        </InfiniteScroll>
    );

    return (
        <CampaignLayout filters={getFiltersComponent()} filtersToolbar={getFiltersToolbar()}>
            {sidebar ? (
                <div className="campaign-applications-with-sidebar">
                    <div className="campaign-applications-with-sidebar__main">{applicationsGrid}</div>
                    <div className="campaign-applications-with-sidebar__aside">{sidebar}</div>
                </div>
            ) : (
                applicationsGrid
            )}
            <ApplicationFilters
                isOpen={filtersModalVisibility}
                onClose={() => showFiltersModal(false)}
                defaultFilters={pinnedFilters}
                resetFilters={resetFilters}
                onApply={(_filters) => {
                    setFilters({ ..._filters, page: 1, sort: pinnedFilters.sort });
                }}
            />
        </CampaignLayout>
    );
};

export default CampaignApplicationsList;
